import { word } from "@/modules/core/utils/i18n"
import {
  MemberSessionHistoryItemEntity,
  MemberSessionHistorySummaryEntity,
} from "@/modules/members/entities/memberSessionHistoryEntity"
import type { DAY_OF_WEEK, SESSION_HISTORY_STATUS } from "@/modules/members/gateway/memberDto"
import dayjs from "dayjs"

// ────────────────────────────────── Item Model ──────────────────────────────────

export type MemberSessionHistoryItemModel = {
  id: string
  programName: string
  programColor: string
  offerName: string
  formattedDate: string
  timeRange: string
  coachName: string | null
  status: SESSION_HISTORY_STATUS
}

export function createSessionHistoryItemModel(entity: MemberSessionHistoryItemEntity): MemberSessionHistoryItemModel {
  return {
    id: entity.id,
    programName: entity.programName,
    programColor: entity.programColor,
    offerName: entity.offerName,
    formattedDate: entity.getFormattedDate(),
    timeRange: entity.getTimeRange(),
    coachName: entity.getCoachFullName(),
    status: entity.status,
  }
}

// ────────────────────────────────── Summary Model ──────────────────────────────────

export type ProgramSummaryModel = {
  name: string
  color: string
  count: number
  percentage: number
}

export type FavoriteSlotModel = {
  programName: string
  programColor: string
  dayOfWeek: DAY_OF_WEEK
  timeRange: string
  count: number
}

export type SessionHistorySummaryModel = {
  totalSessions: number
  attendedCount: number
  noShowCount: number
  programs: ProgramSummaryModel[]
  favoriteSlots: FavoriteSlotModel[]
}

export function createSessionHistorySummaryModel(
  entity: MemberSessionHistorySummaryEntity,
): SessionHistorySummaryModel {
  const allPrograms: ProgramSummaryModel[] = entity.programs.map((p) => ({
    name: p.name,
    color: p.color,
    count: p.count,
    percentage: entity.getProgramPercentage(p.name),
  }))

  const programs =
    allPrograms.length > 5
      ? [
          ...allPrograms.slice(0, 4),
          {
            name: word("box.member.table.history.summary.others"),
            color: allPrograms[4]?.color ?? "",
            count: allPrograms.slice(4).reduce((acc, p) => acc + p.count, 0),
            percentage: Math.round(allPrograms.slice(4).reduce((acc, p) => acc + p.percentage, 0)),
          },
        ]
      : allPrograms

  return {
    totalSessions: entity.totalSessions,
    attendedCount: entity.attendedCount,
    noShowCount: entity.noShowCount,
    programs,
    favoriteSlots: entity.favoriteSlots
      .map((slot) => ({
        programName: slot.name,
        programColor: slot.color,
        dayOfWeek: slot.dayOfWeek,
        timeRange: `${dayjs(slot.startTime, "HH:mm:ss").format("LT")} - ${dayjs(slot.endTime, "HH:mm:ss").format("LT")}`,
        count: slot.count,
      }))
      .sort((a, b) => b.count - a.count),
  }
}
